// Fills a [passport: ...] embed in an article with the products from a public
// Passport list. The list lives in the Passport app, so it is read from the
// app proxy's JSON response. Cards mirror snippets/bc-article-card.liquid.

const requests = new Map();

function load(src) {
  if (!requests.has(src)) {
    requests.set(
      src,
      fetch(src, { headers: { Accept: 'application/json' }, credentials: 'same-origin' }).then((response) => {
        if (!response.ok) throw new Error(`Passport list ${response.status}`);
        return response.json();
      })
    );
  }
  return requests.get(src);
}

function el(tag, className, text) {
  const node = document.createElement(tag);
  if (className) node.className = className;
  if (text) node.textContent = text;
  return node;
}

function safeImage(value) {
  try {
    const url = new URL(value, location.origin);
    return url.protocol === 'https:' && url.hostname === 'cdn.shopify.com' ? url.href : null;
  } catch {
    return null;
  }
}

class PassportList extends HTMLElement {
  connectedCallback() {
    if (this.dataset.ready) return;
    this.dataset.ready = 'true';
    this.render();
  }

  async render() {
    const items = this.querySelector('[data-passport-items]');
    const status = this.querySelector('[data-passport-status]');
    const limit = Math.max(1, Math.min(12, Number(this.dataset.limit) || 6));
    const money = new Intl.NumberFormat(this.dataset.locale || 'en', {
      style: 'currency',
      currency: this.dataset.currency || 'USD',
    });

    status.textContent = 'Loading list';
    try {
      if (!/^\/apps\/passport\/[a-z0-9-]+\/lists\/[a-z0-9-]+$/.test(this.dataset.src || '')) throw new Error('Invalid list path');
      const { list } = await load(this.dataset.src);
      if (list?.canEmbed !== true || !Array.isArray(list.items)) throw new Error('List is not publicly embeddable');

      const title = this.querySelector('[data-passport-title]');
      if (title && list.title && !this.hasAttribute('data-keep-heading')) title.textContent = list.title;

      const products = list.items
        .filter((item) => item.handle && /^[a-z0-9][a-z0-9-]*$/.test(item.handle) && item.title)
        .filter((item) => item.status === 'active' || item.status === 'unlisted')
        .filter((item) => item.availability !== 'unavailable')
        .slice(0, limit);

      this.replaceCards(items, products.map((item) => this.card(item, money)));

      if (products.length) {
        status.textContent = `${products.length} products loaded`;
        status.classList.add('visually-hidden');
      } else {
        status.classList.remove('visually-hidden');
        status.textContent = 'This list has no products to show right now.';
      }
    } catch {
      this.replaceCards(items, []);
      const title = this.querySelector('[data-passport-title]');
      if (title) title.textContent = 'Passport list';
      status.classList.remove('visually-hidden');
      status.textContent = 'This list could not load. Use the link above to view it.';
    }
  }

  replaceCards(items, cards) {
    const active = items.contains(document.activeElement) ? document.activeElement.closest('a') : null;
    const href = active?.getAttribute('href');
    items.replaceChildren(...cards);
    if (active) {
      const replacement = [...items.querySelectorAll('a')].find((link) => link.getAttribute('href') === href);
      (replacement || this.querySelector('.bc-embed__all'))?.focus({ preventScroll: true });
    }
  }

  card(item, money) {
    const card = el('li', 'bc-card');
    const link = el('a', 'bc-card__link');
    const rawRoot = this.dataset.rootUrl || '/';
    const parts = rawRoot.split('/').filter(Boolean);
    const root = rawRoot.startsWith('/') && parts.every((part) => /^[a-z0-9-]+$/i.test(part))
      ? `/${parts.join('/')}${parts.length ? '/' : ''}` : '/';
    link.href = `${root}products/${encodeURIComponent(item.handle)}`;

    const media = el('span', 'bc-card__media');
    const src = safeImage(item.image);
    if (src) {
      const img = document.createElement('img');
      img.src = src;
      img.alt = '';
      img.loading = 'lazy';
      img.width = 600;
      img.height = 600;
      media.append(img);
    }
    link.append(media);
    if (item.maker) link.append(el('span', 'bc-card__maker', item.maker));
    link.append(el('span', 'bc-card__title', item.title));
    card.append(link);

    if (item.note) card.append(el('p', 'bc-card__note', item.note));

    const price = Number(item.price);
    if (item.availableForSale === false) {
      card.append(el('p', 'bc-card__price', 'Sold out'));
    } else if (this.dataset.currency === this.dataset.shopCurrency && item.price !== null && item.price !== '' && Number.isFinite(price)) {
      card.append(el('p', 'bc-card__price', money.format(price)));
    }
    return card;
  }
}

if (!customElements.get('bc-passport-list')) customElements.define('bc-passport-list', PassportList);
